import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getInitials, formatHandicap } from '../utils/format';
import Icon from '../components/Icon';
import './ConnectFromQR.css';

const firstName = (name) => name?.split(' ')[0] || '';

/* ─── The golfer, laid out like a scorecard ──────────────────────────────── */
function GolferScorecard({ golfer }) {
  const handicap = formatHandicap(golfer.handicap);
  const location = [golfer.city, golfer.state].filter(Boolean).join(', ');
  const cells = [
    handicap && ['Handicap', handicap, true],
    golfer.home_course && ['Home course', golfer.home_course, false],
  ].filter(Boolean);

  return (
    <div className="qr-scorecard">
      <div className="qr-scorecard-head">
        <div className="avatar avatar-lg">
          {golfer.photo_url
            ? <img src={golfer.photo_url} alt="" />
            : getInitials(golfer.name)}
        </div>
        <div className="qr-scorecard-who">
          <p className="qr-scorecard-name">{golfer.name}</p>
          {location && <p className="qr-scorecard-loc"><Icon name="pin" size={14} /> {location}</p>}
        </div>
      </div>
      {cells.length > 0 && (
        <div className="qr-scorecard-cells" style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }}>
          {cells.map(([label, value, big]) => (
            <div key={label} className="qr-scorecard-cell">
              <span className="qr-scorecard-label">{label}</span>
              <span className={big ? 'qr-scorecard-big' : 'qr-scorecard-value'}>{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScanStamp() {
  const day = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return (
    <p className="qr-scan-line">
      <Icon name="qr" size={14} /> Scanned with around · {day}
    </p>
  );
}

/* ─── Non-authenticated landing page ─────────────────────────────────────── */
function QRLandingPage({ userId, qrKey }) {
  const [golfer, setGolfer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Go through the shared API client — a raw fetch('/api/…') only works
    // behind the Vercel rewrite and silently breaks when REACT_APP_API_URL
    // points elsewhere (this is the QR/bag-tag first-touch page).
    api.getPublicUser(userId)
      .then(data => setGolfer(data))
      .catch(() => setGolfer(null))
      .finally(() => setLoading(false));
  }, [userId]);

  // Keep the signed key through sign-up / log-in so the scan still connects
  // instantly once they have an account
  const back = encodeURIComponent(`/connect/${userId}${qrKey ? `?k=${encodeURIComponent(qrKey)}` : ''}`);

  return (
    <div className="qr-connect-page">
      <div className="qr-connect-container">
        <header className="qr-topbar">
          <span className="qr-wordmark">around</span>
          <Link to={`/login?redirect=${back}`} className="qr-topbar-link">Log in</Link>
        </header>

        {loading ? (
          <div className="loading-container"><div className="spinner"></div></div>
        ) : golfer ? (
          <>
            <ScanStamp />
            <p className="qr-kicker">You just played with</p>
            <h1 className="qr-name">{golfer.name}</h1>

            <GolferScorecard golfer={golfer} />

            {/* No native app in the stores yet — the web sign-up is the path */}
            <Link to={`/signup?redirect=${back}`} className="btn btn-primary btn-block btn-lg qr-cta">
              Connect with {firstName(golfer.name)}
            </Link>
            <p className="qr-cta-note">
              New to around? Make a free account in about 30 seconds and you're connected with{' '}
              {firstName(golfer.name)} — no request to wait on.
            </p>
            <p className="qr-cta-alt">
              Already have an account? <Link to={`/login?redirect=${back}`}>Log in to connect</Link>
            </p>
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon"><Icon name="flag" size={24} /></div>
            <p className="empty-state-title">Golfer not found</p>
            <p className="empty-state-text">This code may be out of date. Ask them to show you their QR again.</p>
            <Link to="/" className="btn btn-primary" style={{ marginTop: 16 }}>Go to around</Link>
          </div>
        )}

        <p className="qr-signoff"><span>never lose a golf buddy</span></p>
      </div>
    </div>
  );
}

/* ─── Main ConnectFromQR ─────────────────────────────────────────────────── */
function ConnectFromQR() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const qrKey = searchParams.get('k');
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [targetUser, setTargetUser] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  // Set when a signed QR scan connected us on load (no request step)
  const [autoConnected, setAutoConnected] = useState(false);
  const [connectionId, setConnectionId] = useState(null);
  const [memorySaving, setMemorySaving] = useState(false);
  const [memorySaved, setMemorySaved] = useState(false);

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });
  const todayShort = new Date().toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric'
  });

  const [course, setCourse] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (authLoading) return;

    // Not authenticated → show landing page (no redirect)
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    if (user?.id === userId) {
      navigate('/profile');
      return;
    }

    const load = async () => {
      try {
        const [profile, status] = await Promise.all([
          api.getUser(userId),
          api.getConnectionStatus(userId),
        ]);
        setTargetUser(profile);
        if (profile.home_course) setCourse(profile.home_course);

        // A scan of their on-screen code carries a signed key: connect now.
        // A bad or old key just falls through to the request form below.
        if (qrKey && status.status !== 'accepted' && status.status !== 'blocked') {
          try {
            const result = await api.connectViaQr(userId, qrKey);
            setConnectionId(result.connection?.id || null);
            setConnectionStatus({ status: 'accepted' });
            if (!result.alreadyConnected) setAutoConnected(true);
            return;
          } catch {
            // fall back to the request flow
          }
        }
        setConnectionStatus(status);
      } catch (err) {
        setError('Could not find this golfer.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, user, isAuthenticated, authLoading, navigate, qrKey]);

  const memoryNote = () => (note
    ? `Met on ${today}${course ? ` at ${course}` : ''}. ${note}`
    : `Met on ${today}${course ? ` at ${course}` : ''}.`);

  const handleSaveMemory = async (e) => {
    e.preventDefault();
    if (!connectionId) return;
    setMemorySaving(true);
    setError('');
    try {
      await api.saveConnectionMemory(connectionId, { metAtCourse: course || null, note: memoryNote() });
      setMemorySaved(true);
    } catch (err) {
      setError(err.message || 'Could not save.');
    } finally {
      setMemorySaving(false);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.sendConnectionRequest(userId, course, memoryNote());
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to send request.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Not authenticated: show landing page ── */
  if (!authLoading && !isAuthenticated) {
    return <QRLandingPage userId={userId} qrKey={qrKey} />;
  }

  if (loading || authLoading) {
    return (
      <div className="qr-connect-page">
        <div className="loading-container" style={{ minHeight: '100vh' }}>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error && !targetUser) {
    return (
      <div className="qr-connect-page">
        <div className="qr-connect-container">
          <div className="empty-state">
            <div className="empty-state-icon"><Icon name="flag" size={24} /></div>
            <p className="empty-state-title">Golfer not found</p>
            <p className="empty-state-text">{error}</p>
            <Link to="/" className="btn btn-primary" style={{ marginTop: 16 }}>Go Home</Link>
          </div>
        </div>
      </div>
    );
  }

  /* ── Success screen (request sent, or connected instantly by scan) ── */
  if (success || autoConnected) {
    return (
      <div className="qr-connect-page">
        <div className="qr-connect-container">
          <div className="qr-success">
            <div className="qr-success-burst">
              <div className="qr-success-ring"></div>
              <div className="qr-success-check"><Icon name="check" size={38} strokeWidth={2.2} /></div>
            </div>
            <h1 className="qr-success-title">{autoConnected ? "You're connected" : 'Request sent'}</h1>
            <p className="qr-success-subtitle">
              {autoConnected
                ? `You and ${firstName(targetUser?.name)} will see each other's open tee times.`
                : `Once ${firstName(targetUser?.name)} accepts, you'll see each other's open tee times.`}
            </p>

            {/* Round memory, filed like a scorecard */}
            <div className="qr-memory-card">
              <div className="qr-memory-header">
                <span>Round memory</span>
                <span>{todayShort}</span>
              </div>
              <div className="qr-memory-row">
                <div className="avatar">
                  {targetUser?.photo_url
                    ? <img src={targetUser.photo_url} alt="" />
                    : getInitials(targetUser?.name)}
                </div>
                <div className="qr-memory-info">
                  <p className="qr-memory-name">{targetUser?.name}</p>
                  {formatHandicap(targetUser?.handicap) && (
                    <p className="qr-memory-hdcp">Hdcp {formatHandicap(targetUser.handicap)}</p>
                  )}
                </div>
              </div>
              {autoConnected && !memorySaved ? (
                <form className="qr-memory-form" onSubmit={handleSaveMemory}>
                  <label className="form-label" htmlFor="qr-memory-course">Where did you play?</label>
                  <input
                    id="qr-memory-course"
                    type="text"
                    className="form-input"
                    value={course}
                    onChange={(e) => setCourse(e.target.value)}
                    placeholder="e.g. Pebble Beach Golf Links"
                  />
                  <label className="form-label" htmlFor="qr-memory-note">Add a note (optional)</label>
                  <input
                    id="qr-memory-note"
                    type="text"
                    className="form-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Great round, let's play again!"
                  />
                  {error && <div className="auth-error">{error}</div>}
                  <button type="submit" className="btn btn-secondary btn-block" disabled={memorySaving}>
                    {memorySaving ? 'Saving…' : 'Save to round memory'}
                  </button>
                </form>
              ) : (course || note) && (
                <div className="qr-memory-details">
                  {course && (
                    <div className="qr-memory-detail">
                      <Icon name="flag" size={15} />
                      <span>{course}</span>
                    </div>
                  )}
                  {note && (
                    <div className="qr-memory-detail qr-memory-note">
                      <Icon name="chat" size={15} />
                      <span>"{note}"</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="qr-success-actions">
              <Link
                to="/"
                state={{ openPostModal: true, postType: 'round_log', prefillCourse: course }}
                className="btn btn-primary btn-lg"
              >
                <Icon name="scorecard" size={18} /> Log this round
              </Link>
              <Link to="/connections" className="btn btn-secondary">
                View connections
              </Link>
            </div>
            <Link to="/" className="qr-success-skip">Skip for now</Link>
          </div>
        </div>
      </div>
    );
  }

  const alreadyConnected = connectionStatus?.status === 'accepted';
  const requestPending = connectionStatus?.status === 'pending';

  return (
    <div className="qr-connect-page">
      <div className="qr-connect-container">
        <header className="qr-topbar">
          <Link to="/" className="qr-topbar-link qr-back-link"><Icon name="arrowLeft" size={16} /> Back</Link>
          <span className="qr-wordmark qr-wordmark-sm">around</span>
        </header>

        <ScanStamp />
        <p className="qr-kicker">You just played with</p>
        <h1 className="qr-name">{targetUser?.name}</h1>

        {targetUser && <GolferScorecard golfer={targetUser} />}

        {/* Connection state */}
        {alreadyConnected ? (
          <div className="qr-status">
            <Icon name="userCheck" size={20} />
            <span>You're already connected with {firstName(targetUser?.name)}.</span>
            <Link to={`/golfer/${userId}`} className="btn btn-secondary btn-sm">Profile</Link>
          </div>
        ) : requestPending ? (
          <div className="qr-status">
            <Icon name="clock" size={20} />
            <span>A connection request is already pending.</span>
            <Link to="/connections" className="btn btn-secondary btn-sm">View</Link>
          </div>
        ) : (
          <form onSubmit={handleConnect} className="qr-connect-form">
            <div className="qr-round-date">
              <Icon name="calendar" size={20} />
              <div>
                <p className="eyebrow">Today's round</p>
                <p className="qr-round-date-value">{today}</p>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="qr-course">Where did you play?</label>
              <input
                id="qr-course"
                type="text"
                className="form-input"
                value={course}
                onChange={(e) => setCourse(e.target.value)}
                placeholder="e.g. Pebble Beach Golf Links"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="qr-note">Add a note (optional)</label>
              <input
                id="qr-note"
                type="text"
                className="form-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Great round, let's play again!"
              />
            </div>

            {error && <div className="auth-error">{error}</div>}

            <button
              type="submit"
              className="btn btn-primary btn-block btn-lg"
              disabled={submitting}
            >
              {submitting ? 'Sending...' : `Connect with ${firstName(targetUser?.name)}`}
            </button>

            <p className="qr-cta-note">
              {firstName(targetUser?.name)} gets a request and needs to accept it.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default ConnectFromQR;
